import { z } from "zod";
import { NarrativeTextureSchema } from "./narrator-expression.js";
import { SymbolRefSchema, DrawWeightSchema, EnvironmentViewSchema } from "./environment-view.js";
import { ClearanceSchema, StationRefSchema } from "./story-event.js";
/**
 * AUTHORED ENVIRONMENT CONTENT (ADR 0009): the declarative JSON under
 * environments/ that the Tier-A shell generator and the projection consume.
 * The SHELL IS GENERATED — slabs, columns, ceiling grid, mullions,
 * partitions, doors, stairs, lift shaft, service void, dimension lines,
 * title block, ghost exterior — all arithmetic from this block, so a
 * three-bay clinic and a six-bay office are the same generator with
 * different numbers and cannot drift apart. Hand-authored shell geometry in
 * an environment JSON is a validation error; if this schema can't express a
 * shell you need, extend the schema (and raise it), don't inline paths.
 *
 * Units are cm at 1:50, matching the asset contract.
 */
const TokenSchema = z.string().regex(/^[a-z][a-z0-9-]*$/, "must be a lowercase token");
const CmSchema = z.number().finite();
/** A placement slot inside a room: where an actor stands/sits or a prop rests. */
const RoomSlotSchema = z.strictObject({
    id: TokenSchema,
    /** cm from the building's left datum. */
    x: CmSchema,
    /** cm above the room's floor line (0 = on the floor). */
    y: CmSchema.default(0),
    kind: z.enum(["stand", "seat", "surface", "wall", "floor"]),
});
const RoomSchema = z.strictObject({
    id: TokenSchema,
    /** Diegetic name, drawn as a room label in the section's title-block style. */
    label: z.string().min(1),
    /** Which structural bays the room spans, 0-based inclusive. */
    bays: z.strictObject({ from: z.number().int().min(0), to: z.number().int().min(0) }),
    /** Floor index, 0 = the section's primary floor; -1 = the basement (only with shell.basement). */
    floor: z.number().int().min(-1).default(0),
    slots: z.array(RoomSlotSchema),
    /** Initial drawing weight; "ghost" = undiscovered until the projection inks it in. */
    initialWeight: DrawWeightSchema.default("obj"),
    /**
     * DEPTH (ADR 0010): "plane" is cut by the section and drawn in ink;
     * "behind" is seen THROUGH an opening (the core across a courtyard, the
     * street beyond the glazing) and drawn in the behind-the-plane tone at
     * reduced opacity. A behind room gets no partitions and no label.
     */
    depth: z.enum(["plane", "behind"]).default("plane"),
    /** Open plan: no bay-line columns inside the room's span on its floor. */
    openPlan: z.boolean().default(false),
    /** Draw the ceiling service-void line over this room (off where tall shopfronts rise into it). */
    serviceVoid: z.boolean().default(true),
});
/**
 * A WING (ADR 0010): a run of bays that carries floors. Bays that belong to
 * no wing are open courtyards — no slabs above ground, glazed faces on both
 * sides, sky above. A wing with fewer storeys than the building's `floors`
 * carries a roof terrace on its top slab (parapet on the outer edge,
 * balustrade on the courtyard edge).
 */
const WingSchema = z.strictObject({
    id: TokenSchema,
    bays: z.strictObject({ from: z.number().int().min(0), to: z.number().int().min(0) }),
    floors: z.number().int().min(1).max(6),
});
/** Shell features toggled/positioned per environment — all generated, never drawn by hand. */
const ShellSchema = z.strictObject({
    bays: z.number().int().min(1).max(12),
    bayWidthCm: CmSchema.positive().default(600),
    floorToFloorCm: CmSchema.positive().default(390),
    floors: z.number().int().min(1).max(6).default(1),
    /**
     * Wings, or null for the single wing spanning every bay (the pre-ADR-0010
     * building). Wings may not overlap and must stay inside `bays`.
     */
    wings: z.array(WingSchema).min(1).nullable().default(null),
    /**
     * Bay index of the vertical core (stair + lift), or null for none. A core
     * in a courtyard bay stands BEHIND the plane: drawn in the behind tone
     * with a bridge to the wings at every upper floor.
     */
    coreAtBay: z.number().int().min(0).nullable(),
    /** A car park under the ground slab (the plinth), or null. Rooms on floor -1 live in it. */
    basement: z.strictObject({ heightCm: CmSchema.positive() }).nullable().default(null),
    /**
     * Ghost-weight exterior context per side: a street, a tree line, a
     * neighboring BUILDING face across a narrow gap (which is also where
     * string lights anchor), or nothing.
     */
    exterior: z.strictObject({
        left: z.enum(["street", "trees", "building", "none"]),
        right: z.enum(["street", "trees", "building", "none"]),
    }),
    /** Ceiling service void depth in cm (0 = none drawn). */
    serviceVoidCm: CmSchema.min(0).default(60),
    titleBlockLabel: z.string().min(1),
    /**
     * THE ZOOM (aesthetic pass, 2026-08-27): crop the drawing to these floors
     * (inclusive), with slivers of the floors above and below bleeding in —
     * the mid-rise framing where our office holds two floors of a taller
     * building and the neighbors stay faded context. Null = the whole
     * building with sky and ground margins (the establishing framing).
     */
    focus: z
        .strictObject({
        fromFloor: z.number().int().min(0),
        toFloor: z.number().int().min(0),
        /** How much of the neighboring floors peeks in, cm. */
        bleedCm: CmSchema.min(0).default(85),
    })
        .nullable()
        .default(null),
    /**
     * Ambient sky dressing drawn BEHIND the building: cloud symbols drifting
     * across the drawing, and birds. Positions are deterministic; drift is
     * client animation (transform only, reduced-motion disables it).
     */
    sky: z
        .strictObject({
        cloudRefs: z.array(SymbolRefSchema),
        birds: z.number().int().min(0).max(6),
    })
        .nullable()
        .default(null),
});
/** A fixture instance: wall clock, flip calendar, exit sign, blinds, pendant... */
const FixtureInstanceSchema = z.strictObject({
    id: TokenSchema,
    symbolRef: SymbolRefSchema,
    room: TokenSchema,
    slot: TokenSchema,
    /** Physical state when nothing has projected onto it yet. */
    initialState: TokenSchema,
});
/** Static prop dressing present from the first render. */
const PropInstanceSchema = z.strictObject({
    symbolRef: SymbolRefSchema,
    room: TokenSchema,
    slot: TokenSchema,
    variant: TokenSchema.nullable().default(null),
    /** Mirror the symbol about its own centre (a figure prop turned toward the room). */
    facing: z.enum(["left", "right"]).default("right"),
});
/**
 * Extras policy per room: how many unnamed figures the projection may seat
 * here and from which pose pool. The projection resolves counts/poses/
 * mirroring with the session PRNG (ADR 0009 commitment 3) — the definition
 * only authors the bounds, so a waiting room can be "3 to 9 people" and the
 * seed decides.
 */
const ExtrasPolicySchema = z.strictObject({
    room: TokenSchema,
    min: z.number().int().min(0),
    max: z.number().int().min(0),
    poses: z.array(SymbolRefSchema).min(1),
    /** Pet/companion symbols that may accompany extras here. */
    companions: z.array(SymbolRefSchema),
});
/**
 * A named cast member's possible stations, in narrative order of escalation
 * where order matters (e.g. the sponsor's four stages from absent to the
 * partner's office). The PROJECTION chooses the station from authored tells;
 * this array only declares the physically-available places.
 */
const CastPlacementSchema = z.strictObject({
    /** cast.* symbol id (base pose + signature overlay). */
    symbolRef: SymbolRefSchema,
    /**
     * Which portals this person may pass (ADR 0014): a visitor has "public"
     * access and cannot be routed through a staff door; the firm's own people
     * hold "restricted". A physical badge, never a mechanic.
     */
    clearance: ClearanceSchema.default("staff"),
    stations: z
        .array(z.strictObject({
        id: TokenSchema,
        room: TokenSchema,
        slot: TokenSchema,
        pose: TokenSchema,
    }))
        .min(1),
});
/**
 * A PROLOGUE HERO (phase brief section 8): a fixed, authored scene rendered
 * by the same renderer as the live section — static environment JSON, no
 * data dependency, no request in flight. `treatment` lets a hero commit to
 * a cinematic wash (the night office) the live surface never uses; `glow`
 * names the one lit spot when it does.
 */
export const PrologueSceneSchema = z.strictObject({
    sceneId: z.string().regex(/^hero-[a-z0-9-]+$/),
    definition: z.lazy(() => EnvironmentDefinitionSchema),
    view: EnvironmentViewSchema,
    treatment: z.enum(["day", "night"]).default("day"),
    glow: z.strictObject({ room: TokenSchema, slot: TokenSchema }).nullable().default(null),
});
/**
 * NAVIGATION (ADR 0014): the semantic slot graph the actor planner routes
 * over. Every actor-walkable slot (stand / seat / floor) is a node; slots in
 * the same room are mutually reachable; PORTALS connect a slot in one room
 * to a slot in another (a door, a corridor, the bridge to the core, a lift).
 * `entries` are where an arriving person first appears; `exits` are where a
 * departing one is last seen. Null means the environment has no staged
 * movement yet — the compiler then admits only in-room events.
 */
const PortalSchema = z.strictObject({
    id: TokenSchema,
    from: StationRefSchema,
    to: StationRefSchema,
    kind: z.enum(["door", "corridor", "bridge", "lift", "stair", "threshold"]),
    /** Minimum clearance to pass. */
    access: ClearanceSchema.default("public"),
});
const NavigationSchema = z.strictObject({
    portals: z.array(PortalSchema),
    entries: z.array(StationRefSchema).min(1),
    exits: z.array(StationRefSchema).min(1),
});
/**
 * A PAINTED BACKPLATE (ADR 0015, painted shell): one illustration of the
 * empty building, painted over a raster of this very definition's generated
 * shell so it holds the authored geometry, placed at `frame` (cm, section
 * coordinates) under everything the engine decides. Per treatment. The
 * generated shell stays the fallback and the alignment reference.
 */
export const BackplateSchema = z.strictObject({
    treatment: z.enum(["day", "night"]),
    /** environments/backplates/<envId>.<treatment>.png */
    file: z.string().regex(/^backplates\/[a-z0-9.-]+\.png$/),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** Where the image's edges fall in section cm (1 unit = 1 cm at 1:50). */
    frame: z.strictObject({ x: CmSchema, y: CmSchema, w: CmSchema.positive(), h: CmSchema.positive() }),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
/**
 * A SITE VISIT'S STAGING. A case's site visit is an authored ENVIRONMENT
 * document: a list of moments, each an observation. This says which of those
 * moments the environment stages and who stands where for each. Authored data,
 * read by one generic projection, so a case needs no code of its own. The
 * observation text stays in the document; this holds only its index.
 */
export const VisitStagingSchema = z.strictObject({
    moments: z.array(z.strictObject({
        /** Binding id in the audit of bindings, e.g. "counter-is-quiet". */
        id: TokenSchema,
        /** Index into the ENVIRONMENT document's moments. */
        moment: z.number().int().min(0),
        actors: z.array(z.strictObject({
            /** A cast member uses their own actor id (the last segment of their symbolRef), so the story can address them. */
            instanceId: TokenSchema,
            symbolRef: SymbolRefSchema,
            room: TokenSchema,
            slot: TokenSchema,
            pose: TokenSchema,
            facing: z.enum(["left", "right"]),
            propRefs: z.array(SymbolRefSchema).default([]),
        })),
    })).min(1),
});
export const EnvironmentDefinitionSchema = z.strictObject({
    envId: z.string().regex(/^env\.[a-z0-9.-]+$/, "envId must look like env.office.hq"),
    label: z.string().min(1),
    narrative_palette: z.array(NarrativeTextureSchema).max(8).optional(),
    /** Packs this environment draws from; every symbolRef must resolve in one of them. */
    packs: z.array(TokenSchema).min(1),
    shell: ShellSchema,
    rooms: z.array(RoomSchema).min(1),
    fixtures: z.array(FixtureInstanceSchema),
    props: z.array(PropInstanceSchema),
    extras: z.array(ExtrasPolicySchema),
    cast: z.array(CastPlacementSchema),
    navigation: NavigationSchema.nullable().default(null),
    /** How this case's named people are drawn, by the name the case bible gives them.
     * Carried on the case's own environment and read wherever the case plays, the
     * office included, so a person met in a conversation can be staged there. */
    people: z.array(z.strictObject({
        npc: z.string().min(1),
        symbolRef: SymbolRefSchema,
        /** How this person is met when the learner's own words do not say: in the room, or on the phone. Absent means the phone, because most of a case's people are somewhere else. */
        reach: z.enum(["room", "phone"]).optional(),
    })).optional(),
    /** Present on a case's site-visit environment; absent on the office and on Harborlight's clinic, which predate it. */
    visit: VisitStagingSchema.optional(),
    /** Painted backplates by treatment; absent means the generated shell draws itself. */
    backplates: z.array(BackplateSchema).optional(),
});
//# sourceMappingURL=environment-definition.js.map